import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DatabaseBackup, DownloadCloud, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { downloadBackup } from "@/lib/backup";

const todayStr = () => new Date().toISOString().slice(0, 10);
const STORAGE_KEY = "dvepl_backup_prompt_date";

// Once-a-day data backup reminder. Admins only. Shows one popup per calendar day
// (tracked per browser) until the user downloads or dismisses it.
export default function BackupReminder() {
  const { user } = useAuth();
  const isAdmin = user && user.role !== "member";
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    try {
      if (localStorage.getItem(STORAGE_KEY) !== todayStr()) {
        const t = setTimeout(() => setOpen(true), 1200);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, [isAdmin]);

  const markDone = () => {
    try { localStorage.setItem(STORAGE_KEY, todayStr()); } catch { /* ignore */ }
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const fname = await downloadBackup();
      toast.success(`Backup downloaded — ${fname}`);
      markDone();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the backup. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleLater = () => { markDone(); setOpen(false); };

  if (!isAdmin) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleLater(); }}>
      <DialogContent className="sm:max-w-md bg-white border-slate-200" data-testid="backup-reminder-dialog">
        <DialogHeader>
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-2">
            <DatabaseBackup className="w-6 h-6 text-emerald-600" />
          </div>
          <DialogTitle className="text-slate-900 text-lg">Daily data backup</DialogTitle>
          <DialogDescription className="text-slate-500">
            Keep a safe copy of your data. Download a single Excel file with all your OIHR orders, purchase orders,
            customers, vendors, team, contacts and stock — each on its own tab.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[12px] text-slate-500">
          <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
          Takes a few seconds · file is named with today's date.
        </div>
        <div className="flex items-center justify-end gap-2 mt-2">
          <Button type="button" variant="ghost" data-testid="backup-later-btn" onClick={handleLater} className="text-slate-500 hover:text-slate-700">
            Remind me tomorrow
          </Button>
          <Button type="button" data-testid="backup-download-now-btn" onClick={handleDownload} disabled={busy}
            className="rounded-lg bg-[#22a722] hover:bg-[#1c8f1c] text-white font-semibold gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />} Download Backup Now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
